import React, { useMemo } from "react";
import { Box, Grid, Stack, useTheme } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import SpaIcon from "@mui/icons-material/Spa";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { StatCard, GaugeChart, RadarChart, RiskBreakdownChart, LineChart, CombinedBarLineChart, VerticalBarChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { wellnessOverviewStats, wellnessRadar, riskBreakdown, riskTotal, riskParticipationRate, recoveryTrends, strainRecoveryPatterns, weeklyWorkoutMinutes, departmentPerformance } from "../data";

export const WellnessOverview: React.FC = () => {
  const theme = useTheme();

  const statCards = [
    { metric: wellnessOverviewStats.overall_wellness, icon: SpaIcon },
    { metric: wellnessOverviewStats.avg_recovery, icon: FavoriteIcon },
    { metric: wellnessOverviewStats.avg_sleep_score, icon: BedtimeIcon },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Overall Wellness Score" subtitle="Composite wellbeing gauge.">
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <GaugeChart value={72} min={0} max={100} unit="%" title="Overall Wellness" height={200} />
              </Box>
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Wellness Dimensions" subtitle="Current vs benchmark across key areas.">
              <RadarChart
                data={wellnessRadar}
                seriesKeys={["value", "benchmark"]}
                seriesLabels={["Current", "Benchmark"]}
                metricKey="metric"
                height={300}
                colors={[theme.palette.primary.main, theme.palette.text.disabled]}
              />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Risk Overview" subtitle="Organisation-wide risk breakdown.">
              <RiskBreakdownChart data={riskBreakdown} total={riskTotal} participationRate={riskParticipationRate} />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard title="Recovery & Strain Trends" subtitle="Daily recovery score over time.">
              <LineChart
                data={recoveryTrends}
                xAxisKey="label"
                series={[{ dataKey: "recovery_score", label: "Recovery Score", color: theme.palette.success.main, area: true }]}
                height={300}
                yAxisMin={0}
                yAxisMax={100}
              />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Weekly Workout Minutes" subtitle="Average workout duration by day.">
              <VerticalBarChart
                data={weeklyWorkoutMinutes}
                xAxisKey="day"
                yAxisKey="minutes"
                series={[{ dataKey: "minutes", label: "Minutes", color: theme.palette.info.main }]}
                height={300}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Department Performance" subtitle="Key wellness metrics by department.">
              <CombinedBarLineChart
                data={departmentPerformance}
                xAxisKey="department"
                barSeries={[
                  { dataKey: "recovery", label: "Recovery %", color: theme.palette.success.main },
                  { dataKey: "sleep", label: "Sleep %", color: theme.palette.info.main },
                ]}
                lineSeries={[{ dataKey: "wellness", label: "Wellness Score", color: theme.palette.warning.main }]}
                height={350}
                barYAxisMin={0}
                barYAxisMax={100}
                lineYAxisMin={0}
                lineYAxisMax={100}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
